import { axiosGateway } from "../api/axios";

/**
 * ✅ SIMPLIFIED: Ensures fileUrl points to the gateway proxy
 * @param {string} fileUrl - File URL from various sources
 * @returns {string} - Normalized gateway proxy URL
 */
/**
 * Ensures fileUrl is a correct GATEWAY path (NO double /api)
 * - If axiosGateway baseURL = "/api", we must return "/messages/uploads/..."
 */
export function ensureUploadUrl(fileUrl) {
  if (!fileUrl || typeof fileUrl !== "string") return "";

  const s = fileUrl.trim();

  // absolu / blob / data => OK
  if (/^(https?:\/\/|blob:|data:)/i.test(s)) return s;

  // baseURL réel de axiosGateway
  const base = String(axiosGateway?.defaults?.baseURL || "").replace(/\/+$/, "");
  const baseHasApi = /\/api$/i.test(base);

  // Normalise vers un path pur
  let path = s.replace(/^https?:\/\/[^/]+/i, ""); // enlève host si accident
  if (!path.startsWith("/")) path = "/" + path;

  // ✅ supprime tout double /api
  path = path.replace(/^\/api\/api\//i, "/api/");
  // ✅ si path = "/api/messages/..." => on enlève "/api"
  path = path.replace(/^\/api\/messages\//i, "/messages/");
  // ✅ si path = "/messages/uploads/..." => ok
  if (path.startsWith("/messages/uploads/")) {
    return baseHasApi ? path : "/api" + path;
  }

  // ✅ si path = "/uploads/xxx" (MS direct)
  if (path.startsWith("/uploads/")) {
    const filename = path.replace(/^\/uploads\//, "");
    const finalPath = `/messages/uploads/${encodeURIComponent(filename)}`;
    return baseHasApi ? finalPath : "/api" + finalPath;
  }

  // ✅ si juste filename
  if (!path.slice(1).includes("/") && /\.[a-z0-9]{2,6}$/i.test(path)) {
    const finalPath = `/messages/uploads/${encodeURIComponent(path.slice(1))}`;
    return baseHasApi ? finalPath : "/api" + finalPath;
  }

  // fallback
  return baseHasApi ? path.replace(/^\/api/i, "") : path;
}


/**
 * Check if a file looks like an image based on various indicators
 * @param {Object} obj - Object with fileUrl, fileName, mimeType properties
 * @returns {boolean}
 */
export function looksImageLike(obj = {}) {
  const mime = obj.mimeType || obj.type || "";
  const name = (
    obj.fileName || 
    obj.name || 
    obj.fileUrl || 
    obj.content || 
    ""
  ).toLowerCase();

  // Check MIME type first
  if (/^image\//i.test(mime)) {
    return true;
  }

  // Check file extension
  const imageExtensions = /\.(png|jpe?g|gif|webp|bmp|svg|ico)(\?.*)?$/i;
  return imageExtensions.test(name);
}

/**
 * Check if a file looks like a video based on various indicators
 * @param {Object} obj - Object with fileUrl, fileName, mimeType properties
 * @returns {boolean}
 */
export function looksVideoLike(obj = {}) {
  const mime = obj.mimeType || obj.type || "";
  const name = (
    obj.fileName || 
    obj.name || 
    obj.fileUrl || 
    obj.content || 
    ""
  ).toLowerCase();

  // Check MIME type first
  if (/^video\//i.test(mime)) {
    return true;
  }

  // Check file extension
  const videoExtensions = /\.(mp4|webm|ogg|mov|avi|mkv|flv|wmv|m4v)(\?.*)?$/i;
  return videoExtensions.test(name);
}

/**
 * Check if a file looks like a document based on various indicators
 * @param {Object} obj - Object with fileUrl, fileName, mimeType properties
 * @returns {boolean}
 */
export function looksDocumentLike(obj = {}) {
  const mime = obj.mimeType || obj.type || "";
  const name = (
    obj.fileName || 
    obj.name || 
    obj.fileUrl || 
    obj.content || 
    ""
  ).toLowerCase();

  // Check MIME type first
  if (/^application\/(pdf|msword|vnd\.|octet-stream)/i.test(mime)) {
    return true;
  }

  // Check file extension
  const docExtensions = /\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|csv|rtf)(\?.*)?$/i;
  return docExtensions.test(name);
}

/**
 * Extract filename from various URL formats
 * @param {string} url 
 * @returns {string}
 */
export function extractFilename(url) {
  if (!url) return "";
  
  // Remove query parameters and get last segment
  const cleaned = url.split('?')[0];
  const segments = cleaned.split('/');
  return segments[segments.length - 1] || "";
}

/**
 * Get file extension from filename or URL
 * @param {string} filename 
 * @returns {string}
 */
export function getFileExtension(filename) {
  if (!filename) return "";
  
  const match = filename.match(/\.([^.]+)$/);
  return match ? match[1].toLowerCase() : "";
}

/**
 * Format file size in human readable format
 * @param {number} bytes 
 * @returns {string}
 */
export function formatFileSize(bytes) {
  if (!bytes || bytes === 0) return "0 B";
  
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/**
 * Debug helper to log file URL transformations
 * @param {string} label 
 * @param {Object} message 
 */
export function debugFileUrl(label, message) {
  if (process.env.NODE_ENV === 'development') {
  }
}

/**
 * Classifie les pièces jointes d'une liste de messages.
 * @param {Array} messages - messages normalisés (useMessaging) { fileUrl, fileName, mimeType, messageType, ... }
 * @param {Object} [opts]
 * @param {"id"|"url"} [opts.dedupeBy="id"] - clé de déduplication
 * @returns {{ media: Array, documents: Array }}
 */
export function classifyAttachments(messages = [], opts = {}) {
  const { dedupeBy = "id" } = opts;
  const media = [];
  const documents = [];
  const seen = new Set();

  for (const m of messages) {
    if (!m?.fileUrl) continue;

    const key =
      dedupeBy === "url"
        ? String(m.fileUrl)
        : String(m.id || m._id || m.fileUrl || Math.random());

    if (seen.has(key)) continue;
    seen.add(key);

    // Normalise l’URL via gateway si besoin
    const url = ensureUploadUrl(m.fileUrl);

    // Détermine le type
    let type = "file";
    if (looksImageLike(m)) type = "image";
    else if (looksVideoLike(m)) type = "video";
    else if (looksDocumentLike(m)) type = "document";

    const item = {
      id: m.id || m._id || key,
      url,
      fileName: m.fileName,
      mimeType: m.mimeType,
      fromUserId: m.fromUserId,
      createdAt: m.createdAt,
      type, // "image" | "video" | "document" | "file"
    };

    if (type === "image" || type === "video") media.push(item);
    else documents.push(item);
  }

  return { media, documents };
}